import { getWebApp } from '../telegram/webapp.js';

/**
 * Session-token storage. In Telegram we persist the refresh token in
 * `CloudStorage` (the web equivalent of the iOS Keychain restore flow); outside
 * Telegram, or if CloudStorage errors, we fall back to `localStorage`.
 */

const PREFIX = 'volt.';
export const CLOUD_STORAGE_TIMEOUT_MS = 1500;

function localGet(key: string): string | null {
  try {
    return window.localStorage.getItem(PREFIX + key);
  } catch {
    return null;
  }
}

function localSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(PREFIX + key, value);
  } catch {
    // storage may be blocked (private mode) — ignore.
  }
}

function localRemove(key: string): void {
  try {
    window.localStorage.removeItem(PREFIX + key);
  } catch {
    // ignore.
  }
}

function withTimeout<T>(promise: Promise<T>, timeoutMs = CLOUD_STORAGE_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error('CloudStorage timeout')), timeoutMs);
    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
}

export async function secureGet(key: string): Promise<string | null> {
  const cloud = getWebApp()?.CloudStorage;
  if (cloud) {
    try {
      const value = await withTimeout(new Promise<string>((resolve, reject) => {
        cloud.getItem(key, (error, result) => (error ? reject(new Error(error)) : resolve(result)));
      }));
      if (value) return value;
    } catch (error) {
      console.warn('CloudStorage getItem failed, using localStorage', error);
    }
  }
  return localGet(key);
}

export async function secureSet(key: string, value: string): Promise<void> {
  localSet(key, value);
  const cloud = getWebApp()?.CloudStorage;
  if (cloud) {
    try {
      await withTimeout(new Promise<boolean>((resolve, reject) => {
        cloud.setItem(key, value, (error, ok) => (error ? reject(new Error(error)) : resolve(ok)));
      }));
    } catch (error) {
      console.warn('CloudStorage setItem failed', error);
    }
  }
}

export async function secureRemove(key: string): Promise<void> {
  localRemove(key);
  const cloud = getWebApp()?.CloudStorage;
  if (cloud) {
    try {
      await withTimeout(new Promise<boolean>((resolve, reject) => {
        cloud.removeItem(key, (error, ok) => (error ? reject(new Error(error)) : resolve(ok)));
      }));
    } catch (error) {
      console.warn('CloudStorage removeItem failed', error);
    }
  }
}

export const REFRESH_TOKEN_KEY = 'refreshToken';

/** Small synchronous key/value store for non-secret UI preferences. */
export const prefs = {
  get(key: string): string | null {
    return localGet(key);
  },
  set(key: string, value: string): void {
    localSet(key, value);
  },
  remove(key: string): void {
    localRemove(key);
  },
};
